import type { AxiosError } from "axios";

/**
 * Format an error thrown by the axios client into a human-readable string.
 *
 * FastAPI returns validation errors as `{detail: [{msg, loc, ...}]}` — rendering
 * that array directly in JSX crashes React. This helper flattens every shape
 * we might encounter into a single message safe to display.
 */
export function formatApiError(err: unknown, fallback = "Something went wrong. Please try again."): string {
    if (!err) return fallback;

    if (typeof err === "string") return err;

    const axiosErr = err as AxiosError<{ detail?: unknown; message?: string }>;
    const data = axiosErr?.response?.data;

    const detail = data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;

    if (Array.isArray(detail)) {
        const messages = detail
            .map((e) => {
                if (typeof e === "string") return e;
                if (e && typeof (e as { msg?: unknown }).msg === "string") {
                    return (e as { msg: string }).msg;
                }
                return null;
            })
            .filter(Boolean);
        if (messages.length) return messages.join(". ");
    }

    if (
        detail &&
        typeof detail === "object" &&
        "msg" in detail &&
        typeof (detail as { msg?: unknown }).msg === "string"
    ) {
        return (detail as { msg: string }).msg;
    }

    if (typeof data?.message === "string" && data.message.trim()) {
        return data.message;
    }

    if (axiosErr?.message) return axiosErr.message;

    return fallback;
}
